import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  LineItem,
  LineItems,
  MarketAction,
  Package as ProtoPackage,
  PackageService as SDKPackageService,
  PricingDisplayOption,
  Status,
  StatusString,
} from '@vendasta/marketplace-packages';
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { MarketsService } from './markets/markets.service';
import { Package, ResellerItemId } from './package/package';
import { mergeUpdatedArrays } from './utils';

export declare interface PackagesMap {
  [marketId: string]: Package[];
}
@Injectable({ providedIn: 'root' })
export class PackageService {
  get packages(): Observable<PackagesMap> {
    return this.packagesSubject.asObservable();
  }

  constructor(
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
    private sdkService: SDKPackageService,
    private marketService: MarketsService,
  ) {}
  private packagesSubject: BehaviorSubject<PackagesMap> = new BehaviorSubject<PackagesMap>({});
  public recommendedPackages$: Observable<Package[]>;

  private static convertLineItems(lineItems: LineItems): ResellerItemId[] {
    if (!lineItems || !lineItems.lineItems) {
      return [];
    }
    return lineItems.lineItems
      .filter((item) => item && item.id)
      .map((item) => {
        // TODO: unify the way we handle app and addon
        if (!item.parentId) {
          //  app
          return {
            app_id: item.id,
            edition_id: item.editionId,
            quantity: item.quantity,
          };
        }
        // addon
        return {
          app_id: item.parentId,
          addon_id: item.id,
          edition_id: item.editionId,
          quantity: item.quantity,
        };
      });
  }

  static convertPriceFrequencyToLowercase(pkg: Package): Package {
    if (pkg.pricing.prices) {
      for (const price of pkg.pricing.prices) {
        if (price.frequency) {
          price.frequency = price.frequency.toLowerCase();
        }
      }
    }
    return pkg;
  }

  loadAll(marketId?: string, inStoreOrder?: boolean, order?: string[]): void {
    marketId = marketId || 'default';
    this.partnerId$
      .pipe(
        switchMap((partnerId) =>
          this.sdkService.loadAllPackages(
            partnerId,
            [Status.PUBLISHED, Status.DRAFT, Status.ARCHIVED],
            marketId,
            inStoreOrder,
            1000,
          ),
        ),
        take(1),
      )
      .subscribe(
        (packagesData) => {
          if (packagesData.packages) {
            const packages = packagesData.packages.map((packageData) => {
              const storePackage = new Package(packageData);
              if (packageData.lineItems) {
                storePackage.line_items = storePackage.convertSDKLineItemsToProductListWithEdition(
                  packageData.lineItems.lineItems,
                );
              } else {
                storePackage.line_items = [];
              }

              return storePackage;
            });

            if (order) {
              packages.sort(function (a, b): any {
                return order.indexOf(a.solution_id) - order.indexOf(b.solution_id);
              });
            }

            this.updatePackagesForMarket(marketId, packages);
          } else {
            this.updatePackagesForMarket(marketId, []);
          }
        },
        (err) => this.packagesSubject.error(err),
      );
  }

  loadRecommendedPackages(): void {
    this.recommendedPackages$ = this.sdkService.loadAllPackages('VCOR', [Status.PUBLISHED], 'mkplrecpkgs').pipe(
      map((packagesData) => {
        if (packagesData.packages) {
          return packagesData.packages
            .map((packageData) => new Package(packageData))
            .filter((pkg) => (pkg ? pkg.isPublished : false));
        } else {
          return [];
        }
      }),
    );
  }

  getPackagesForMarket$(marketId: string): Observable<ProtoPackage[]> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.sdkService.loadAllPackages(partnerId, [Status.PUBLISHED], marketId)),
      map((response) => response.packages),
    );
  }

  listRecommendedPackages$(): Observable<Package[]> {
    return this.sdkService.loadAllPackages('VCOR', [Status.PUBLISHED], 'mkplrecpkgs').pipe(
      map((packagesData) => {
        if (packagesData.packages) {
          return packagesData.packages
            .map((packageData) => new Package(packageData))
            .filter((pkg) => (pkg ? pkg.isPublished : false));
        } else {
          return [];
        }
      }),
    );
  }

  createPackage(pkg: Package, newEditor = false): Observable<Package> {
    return combineLatest([this.partnerId$, this.marketService.currentMarket$]).pipe(
      switchMap(([partnerId, market]) => {
        if (!partnerId) {
          return throwError('Partner id required.');
        }
        if (!market) {
          return throwError('Market id required.');
        }

        return this.sdkService.create(
          partnerId,
          market.market_id,
          pkg.name,
          pkg.tagline,
          pkg.icon,
          pkg.header_image_url,
          pkg.content,
          newEditor
            ? this.convertLineItemsToSDKLineItems(pkg)
            : pkg.convertProductOrderProductAndAddonKeyListToSDKLineItems(),
          pkg.convertPricingToSDKPricing(),
          pkg.hide_product_icons_and_names,
          pkg.hide_product_details,
          false,
          pkg.market_action.sdkMarketAction || new MarketAction({}),
          pkg.usesBillingPricing,
          pkg.pricingDisplayOption,
        );
      }),
      map((thisPkg) => new Package(thisPkg)),
    );
  }

  updatePackage(pkg: Package, newEditor = false): Observable<Package> {
    return this.sdkService
      .update(
        pkg.solution_id,
        pkg.name,
        pkg.tagline,
        pkg.icon,
        pkg.header_image_url,
        pkg.content,
        newEditor
          ? this.convertLineItemsToSDKLineItems(pkg)
          : pkg.convertProductOrderProductAndAddonKeyListToSDKLineItems(),
        pkg.convertPricingToSDKPricing(),
        pkg.hide_product_icons_and_names,
        pkg.hide_product_details,
        pkg.market_action.sdkMarketAction || new MarketAction({}),
        pkg.usesBillingPricing,
        pkg.pricingDisplayOption || PricingDisplayOption.PRICING_DISPLAY_OPTION_NOT_SPECIFIED,
      )
      .pipe(map((thisPkg) => new Package(thisPkg)));
  }

  private convertLineItemsToSDKLineItems(pkg: Package): LineItem[] {
    return pkg.line_items.map((li) => {
      const sdkLineItem = {
        id: li.addon_id || li.app_id,
        quantity: li.quantity,
      } as LineItem;

      if (li.addon_id) {
        sdkLineItem.parentId = li.app_id;
      } else {
        sdkLineItem.editionId = li.edition_id;
      }
      return sdkLineItem;
    });
  }

  publishPackage(packageId: string): Observable<Package> {
    return this.setStatus(packageId, Status.PUBLISHED);
  }

  unpublishPackage(packageId: string): Observable<Package> {
    return this.setStatus(packageId, Status.DRAFT);
  }

  deletePackage(packageId: string, marketId: string): Observable<HttpResponse<null>> {
    return this.sdkService
      .hideArchivedPackage(packageId)
      .pipe(tap(() => this.removePackageFromMap(packageId, marketId)));
  }

  archivePackage(packageId: string): Observable<Package> {
    return this.setStatus(packageId, Status.ARCHIVED);
  }

  restorePackage(packageId: string): Observable<Package> {
    return this.unpublishPackage(packageId);
  }

  setStatus(packageId: string, status: Status): Observable<Package> {
    return this.sdkService.setStatus(packageId, status).pipe(map((pkg) => new Package(pkg)));
  }

  getPackageById(packageId: string): Observable<Package> {
    return this.sdkService.getMulti([packageId]).pipe(
      map((pkgs) => {
        if (pkgs && pkgs.length) {
          const pkg = new Package(pkgs[0]);
          pkg.line_items = PackageService.convertLineItems(pkgs[0].lineItems);
          return pkg;
        }
        throw new HttpErrorResponse({
          status: 404,
          statusText: 'package with id ' + packageId + " doesn't exist",
        });
      }),
      shareReplay(1),
    );
  }

  copyPackage(
    packageId: string,
    partnerId: string,
    marketIds: string[],
    contactSales: boolean,
    name: string,
    status: string,
    currency?: string,
    price?: number,
    additionalPrice?: number,
    frequency?: string,
    additionalFrequency?: string,
    isStartingPrice?: boolean,
    isAdditionalStartingPrice?: boolean,
  ): Observable<any> {
    return this.sdkService.copyPackage(
      packageId,
      partnerId,
      marketIds,
      contactSales,
      name,
      status as StatusString,
      currency,
      price,
      additionalPrice,
      frequency,
      additionalFrequency,
      isStartingPrice,
      isAdditionalStartingPrice,
    );
  }

  appendPackageToMap(newPkg: Package): void {
    const allPackages = this.packagesSubject.getValue();
    let existingPackages = allPackages[newPkg.market_id];
    if (existingPackages) {
      const pkgIndex = existingPackages.findIndex((pkg) => pkg.solution_id === newPkg.solution_id);
      if (pkgIndex === -1) {
        existingPackages.push(newPkg);
      }
    } else {
      existingPackages = [newPkg];
    }
    allPackages[newPkg.market_id] = existingPackages;
    this.packagesSubject.next(allPackages);
  }

  private updatePackagesForMarket(marketId: string, packages: Package[]): void {
    const allPackages = {
      [marketId]: packages,
    };
    this.packagesSubject.next(allPackages);
  }

  /**
   * Given two lists of packages, merge them into one, containing only the newest unique items from
   * each.
   */
  mergePackages(packages1: Package[], packages2: Package[]): Package[] {
    return mergeUpdatedArrays(packages2, packages1, 'solution_id');
  }

  removePackageFromMap(packageId: string, marketId: string): void {
    const pkgs = this.packagesSubject.getValue();
    if (pkgs && pkgs[marketId]) {
      const marketPkgs = pkgs[marketId] || [];
      const filteredPkgs = marketPkgs.filter((pkg) => pkg.solution_id !== packageId);
      pkgs[marketId] = filteredPkgs;
      this.packagesSubject.next(pkgs);
    }
  }
}
